import React, { useState } from 'react';

interface SearchBarProps {
  onSearch: (filters: SearchFilters) => void;
  onClear: () => void;
  categories: string[];
  totalResults?: number;
}

export interface SearchFilters {
  query: string;
  category?: string;
  priority?: number;
  startDate?: string;
  endDate?: string;
  hasAudio?: boolean;
  processed?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch, onClear, categories, totalResults }) => {
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    category: '',
    priority: undefined,
    startDate: '',
    endDate: '',
    hasAudio: undefined,
    processed: undefined
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(filters);
  };

  const handleClear = () => {
    setFilters({
      query: '',
      category: '',
      priority: undefined,
      startDate: '',
      endDate: '',
      hasAudio: undefined,
      processed: undefined
    });
    onClear();
  };

  const handleQuickFilter = (filterType: string) => {
    const today = new Date();
    const newFilters = { ...filters };

    switch (filterType) {
      case 'last7days':
        const lastWeek = new Date();
        lastWeek.setDate(today.getDate() - 7);
        newFilters.startDate = lastWeek.toISOString().split('T')[0];
        newFilters.endDate = today.toISOString().split('T')[0];
        break;
      case 'highPriority':
        newFilters.priority = 8;
        break;
      case 'unprocessed':
        newFilters.processed = false;
        break;
      case 'hasAudio':
        newFilters.hasAudio = true;
        break;
    }

    setFilters(newFilters);
    onSearch(newFilters);
  };

  return (
    <div className="search-bar">
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-row">
          <input
            type="text"
            placeholder="Search ideas..."
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            className="search-input"
          />
          
          <select
            value={filters.category || ''}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="search-select"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={filters.priority || ''}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value ? Number(e.target.value) : undefined })}
            className="search-select"
          >
            <option value="">All Priorities</option>
            <option value="9">High (9-10)</option>
            <option value="8">High (8)</option>
            <option value="7">Medium-High (7)</option>
            <option value="5">Medium (5-6)</option>
            <option value="3">Low (1-4)</option>
          </select>
        </div>

        <div className="search-row">
          <div className="date-range">
            <input
              type="date"
              placeholder="Start date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="date-input"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              placeholder="End date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="date-input"
            />
          </div>

          <div className="search-actions">
            <button type="submit" className="search-button">
              Search
            </button>
            <button type="button" onClick={handleClear} className="clear-button">
              Clear
            </button>
          </div>
        </div>
      </form>

      <div className="quick-filters">
        <button
          type="button"
          onClick={() => handleQuickFilter('last7days')}
          className="quick-filter-button"
        >
          Last 7 days
        </button>
        <button
          type="button"
          onClick={() => handleQuickFilter('highPriority')}
          className="quick-filter-button"
        >
          High Priority
        </button>
        <button
          type="button"
          onClick={() => handleQuickFilter('unprocessed')}
          className="quick-filter-button"
        >
          Unprocessed
        </button>
        <button
          type="button"
          onClick={() => handleQuickFilter('hasAudio')}
          className="quick-filter-button"
        >
          Has Audio
        </button>
      </div>

      {totalResults !== undefined && (
        <div className="search-results-count">
          Found {totalResults} {totalResults === 1 ? 'idea' : 'ideas'}
        </div>
      )}
    </div>
  );
};